import { isEmpty, keys } from 'lodash'
import isBefore from 'date-fns/isBefore'
import isAfter from 'date-fns/isAfter'

import { STATUS } from 'features/BaseRules/components/Status/constants'
import { sanitizePrice, round } from 'utils/math'
import { ICabinSetting, ICabinStrategy } from 'features/InventoryRules/components/CabinStrategiesForm/types'
import {
  GTInventoryRule,
  GTInventoryRuleCabinSetting,
  GTInventoryRuleCabinSettingInput,
  GTInventoryRuleCabinStrategy
} from 'gql/types.generated'
import { IDaysToDepartureSetting } from 'features/InventoryRules/components/DaysToDepartureSettingsForm/types'
import { EExceptionType } from 'features/InventoryRules/components/NewInventoryRule/types'

export const baseRuleCanBeDeactivated = (baseRule = {}): TSFixMe => {
  // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
  return baseRule.status === STATUS.ACTIVATED || baseRule.status === STATUS.SCHEDULED
}

export const baseRuleCanBeActivated = (baseRule = {}): TSFixMe => {
  // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
  return baseRule.status === STATUS.DEACTIVATED
}

export const baseRuleIsScheduled = (fareRule: TSFixMe): TSFixMe => {
  const now = new Date()

  if (isEmpty(fareRule.activationAtRanges)) {
    return false
  }

  const activatedRange = fareRule.activationAtRanges.find((activationAtRange: TSFixMe): TSFixMe => {
    return isBefore(new Date(activationAtRange.startAt), now) && isAfter(new Date(activationAtRange.endAt), now)
  })

  return isEmpty(activatedRange)
}

export const mapBaseRuleFormToMutation = (baseRule: TSFixMe): TSFixMe => {
  const exceptions = baseRule.exceptions.reduce((result: TSFixMe, exception: TSFixMe): TSFixMe => {
    if (isEmpty(exception.value) || isEmpty(exception.key)) {
      return result
    }

    if (exception.key === EExceptionType.SERVICES) {
      return {
        ...result,
        routeServiceIDs: exception.value.map(({ id }: { id: string }): string => id)
      }
    }
    return {
      ...result,
      [exception.key]: exception.value
    }
  }, {})

  let citySegments = [
    {
      originID: baseRule.origin.id,
      destinationID: baseRule.destination.id,
      segmentIDs: baseRule.allSegmentsAreSelected
        ? []
        : baseRule.segments
            .filter((segment: TSFixMe): TSFixMe => segment.cityOrigin.id === baseRule.origin.id)
            .map(({ id }: TSFixMe): TSFixMe => id),
      routeServiceIDs: baseRule.allServicesAreSelected
        ? []
        : baseRule.routeServices
            .filter((routeService: TSFixMe): TSFixMe => routeService.cityOrigin.id === baseRule.origin.id)
            .map(({ id }: TSFixMe): TSFixMe => id)
    }
  ]

  if (baseRule.isReturn && baseRule.origin.id !== baseRule.destination.id) {
    citySegments = [
      ...citySegments,
      {
        originID: baseRule.destination.id,
        destinationID: baseRule.origin.id,
        segmentIDs: baseRule.allSegmentsAreSelected
          ? []
          : baseRule.segments
              .filter((segment: TSFixMe): TSFixMe => segment.cityOrigin.id === baseRule.destination.id)
              .map(({ id }: TSFixMe): TSFixMe => id),
        routeServiceIDs: baseRule.allServicesAreSelected
          ? []
          : baseRule.routeServices
              .filter((routeService: TSFixMe): TSFixMe => routeService.cityOrigin.id === baseRule.destination.id)
              .map(({ id }: TSFixMe): TSFixMe => id)
      }
    ]
  }

  return {
    companyID: baseRule.company.id,
    name: baseRule.name,
    description: baseRule.description,
    activationAtRanges: baseRule.activationAtRanges.map(({ startAt, endAt }: TSFixMe): TSFixMe => {
      return {
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString()
      }
    }),
    departureAtRanges: baseRule.departureAtRanges.map(({ startAt, endAt }: TSFixMe): TSFixMe => {
      return {
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString()
      }
    }),
    exceptions,
    citySegments
  }
}

export const mapFareRuleFormToMutation = (fareRule: TSFixMe): TSFixMe => {
  return {
    ...mapBaseRuleFormToMutation(fareRule),
    price: sanitizePrice(fareRule.price * 100, true)
  }
}

export const mapCabinSettingFormToMutation = (cabinSetting: ICabinSetting): GTInventoryRuleCabinSettingInput => {
  return {
    bookingClassID: cabinSetting.bookingClassId,
    isActive: cabinSetting.isActive,
    maxLoadFactor: round((cabinSetting.maxLoadFactor as number) * 100),
    promo: cabinSetting.promo
  }
}

export const mapInventoryRuleFormToMutation = (inventoryRule: TSFixMe): TSFixMe => {
  return {
    ...mapBaseRuleFormToMutation(inventoryRule),
    cabinStrategies: inventoryRule.cabinStrategies.map((cabinStrategy: ICabinStrategy, strategyIndex: number) => {
      const daysToDepartureByCabinStrategy: IDaysToDepartureSetting[] = inventoryRule.daysToDepartureSettings.filter(
        (daysToDepartureSetting: IDaysToDepartureSetting) => daysToDepartureSetting.cabinStrategyIndex === strategyIndex
      )
      const activationRanges = daysToDepartureByCabinStrategy.map((daysToDepartureSetting) => {
        return {
          start: daysToDepartureSetting.start,
          end: daysToDepartureSetting.end
        }
      })

      return {
        id: cabinStrategy.id,
        cabinSettings: cabinStrategy.cabinSettings.map(mapCabinSettingFormToMutation),
        activationRanges
      }
    })
  }
}

export const mapBaseRuleQueryToForm = (baseRule: TSFixMe): TSFixMe => {
  const departureAtRanges = baseRule.departureAtRanges || []
  const activationAtRanges = baseRule.activationAtRanges || []

  return {
    company: {
      id: baseRule.companyID ? baseRule.companyID : ''
    },
    name: baseRule.name,
    description: baseRule.description,
    isReturn: baseRule.citySegments.length === 2,
    departureAtRanges: departureAtRanges.map(({ startAt, endAt }: TSFixMe): TSFixMe => {
      return {
        startAt: new Date(startAt),
        endAt: new Date(endAt)
      }
    }),
    activationAtRanges: activationAtRanges.map(({ startAt, endAt }: TSFixMe): TSFixMe => {
      return {
        startAt: new Date(startAt),
        endAt: new Date(endAt)
      }
    }),
    exceptions: keys(baseRule.exceptions)
      .filter((key): TSFixMe => key !== '__typename' && !isEmpty(baseRule.exceptions[key]))
      .map((key): TSFixMe => {
        return {
          key,
          value: baseRule.exceptions[key]
        }
      }),
    origin: baseRule.citySegments[0].origin,
    destination: baseRule.citySegments[0].destination,
    allSegmentsAreSelected: baseRule.citySegments[0].allSegments,
    segments: baseRule.citySegments.reduce((result: TSFixMe, citySegment: TSFixMe): TSFixMe => {
      return [
        ...result,
        ...citySegment.segments.map((segment: TSFixMe): TSFixMe => {
          return {
            ...segment,
            cityOrigin: citySegment.origin,
            cityDestination: citySegment.destination
          }
        })
      ]
    }, []),
    segmentsOrigins: baseRule.citySegments.reduce((result: TSFixMe, citySegment: TSFixMe, index: TSFixMe): TSFixMe => {
      citySegment.segments.forEach((segment: TSFixMe): TSFixMe => {
        if (index === 0 && isEmpty(result.find(({ id }: TSFixMe): TSFixMe => id === segment.origin.id))) {
          result = [...result, segment.origin]
        } else if (index === 1 && isEmpty(result.find(({ id }: TSFixMe): TSFixMe => id === segment.destination.id))) {
          result = [...result, segment.destination]
        }
      })

      return result
    }, []),
    segmentsDestinations: baseRule.citySegments.reduce(
      (result: TSFixMe, citySegment: TSFixMe, index: TSFixMe): TSFixMe => {
        citySegment.segments.forEach((segment: TSFixMe): TSFixMe => {
          if (index === 0 && isEmpty(result.find(({ id }: TSFixMe): TSFixMe => id === segment.destination.id))) {
            result = [...result, segment.destination]
          } else if (index === 1 && isEmpty(result.find(({ id }: TSFixMe): TSFixMe => id === segment.origin.id))) {
            result = [...result, segment.origin]
          }
        })

        return result
      },
      []
    ),
    allServicesAreSelected: baseRule.citySegments[0].allRouteServices,
    routeServices: baseRule.citySegments.reduce((result: TSFixMe, citySegment: TSFixMe): TSFixMe => {
      return [
        ...result,
        ...citySegment.routeServices.map((routeService: TSFixMe): TSFixMe => {
          return {
            ...routeService,
            cityOrigin: citySegment.origin,
            cityDestination: citySegment.destination
          }
        })
      ]
    }, [])
  }
}

export const mapFareRuleQueryToForm = (fareRule: TSFixMe): TSFixMe => {
  return {
    ...mapBaseRuleQueryToForm(fareRule),
    price: sanitizePrice(fareRule.price / 100)
  }
}

export const mapCabinSettingQueryToForm = (cabinSetting: GTInventoryRuleCabinSetting): ICabinSetting => {
  return {
    bookingClassId: cabinSetting.bookingClass.id,
    name: cabinSetting.promo ? `${cabinSetting.bookingClass.name}P` : cabinSetting.bookingClass.name,
    priority: cabinSetting.bookingClass.priority,
    isActive: cabinSetting.isActive,
    maxLoadFactor: round(cabinSetting.maxLoadFactor / 100, 2),
    promo: cabinSetting.promo
  }
}

export const mapInventoryRuleQueryToForm = (inventoryRule: GTInventoryRule): TSFixMe => {
  const daysToDepartureSettings: IDaysToDepartureSetting[] = inventoryRule.cabinStrategies
    .reduce((result: TSFixMe[], cabinStrategy, cabinStrategyIndex) => {
      if (cabinStrategy.isDefault) return result

      return [
        ...result,
        ...cabinStrategy.activationRanges.map((activationRange) => {
          return {
            cabinStrategyIndex,
            start: activationRange.start,
            end: activationRange.end
          }
        })
      ]
    }, [])
    .sort((a, b) => a.start - b.start)
  return {
    ...mapBaseRuleQueryToForm(inventoryRule),
    cabinStrategies: inventoryRule.cabinStrategies.map(
      (cabinStrategy: GTInventoryRuleCabinStrategy): ICabinStrategy => {
        return {
          id: cabinStrategy.id,
          isDefault: cabinStrategy.isDefault,
          cabinSettings: cabinStrategy.cabinSettings.map(mapCabinSettingQueryToForm)
        }
      }
    ),
    daysToDepartureSettings
  }
}
